import React, { useState } from "react";
import {
  Cloud,
  CloudOff,
  RefreshCw,
  Check,
  AlertTriangle,
  Wifi,
  WifiOff,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

/**
 * Sync Status Indicator - Floating component in bottom-right corner
 * Shows: 🟢 Online (synced), 🟡 Online (syncing/unsynced), 🔴 Offline
 */
const SyncStatusIndicator = ({
  isOnline,
  isSynced,
  isSyncing,
  lastSyncTime,
  onManualSync,
  hasUnsyncedChanges,
  storageMode,
  error,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine status
  const getStatus = () => {
    if (!isOnline) {
      return {
        color: "bg-red-500",
        pulseColor: "bg-red-400",
        icon: CloudOff,
        text: "Offline",
        description: "Changes saved locally",
      };
    }
    if (isSyncing) {
      return {
        color: "bg-yellow-500",
        pulseColor: "bg-yellow-400",
        icon: RefreshCw,
        text: "Syncing",
        description: "Saving to cloud...",
        animate: true,
      };
    }
    if (hasUnsyncedChanges) {
      return {
        color: "bg-yellow-500",
        pulseColor: "bg-yellow-400",
        icon: AlertTriangle,
        text: "Unsynced",
        description: "Changes pending sync",
      };
    }
    if (error) {
      return {
        color: "bg-orange-500",
        pulseColor: "bg-orange-400",
        icon: AlertTriangle,
        text: "Error",
        description: error,
      };
    }
    return {
      color: "bg-green-500",
      pulseColor: "bg-green-400",
      icon: Check,
      text: "Synced",
      description: "All changes saved",
    };
  };

  const status = getStatus();
  const StatusIcon = status.icon;

  // Format last sync time
  const formatLastSync = (timestamp) => {
    if (!timestamp) return "Never synced";
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {/* Expanded panel */}
      {isExpanded && (
        <div className="bg-gray-800/95 backdrop-blur-sm border border-gray-700 rounded-2xl p-4 shadow-2xl min-w-[240px] max-w-[calc(100vw-2rem)] animate-fade-in">
          {/* Status header */}
          <div className="flex items-center gap-3 mb-3 pb-3 border-b border-gray-700">
            <div className={`w-3 h-3 rounded-full ${status.color}`} />
            <div>
              <div className="text-white font-semibold text-sm">{status.text}</div>
              <div className="text-gray-400 text-xs">{status.description}</div>
            </div>
          </div>

          {/* Connection info */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 flex items-center gap-1">
                {isOnline ? (
                  <Wifi className="w-3 h-3 text-green-400" />
                ) : (
                  <WifiOff className="w-3 h-3 text-red-400" />
                )}
                Connection
              </span>
              <span className={isOnline ? "text-green-400" : "text-red-400"}>
                {isOnline ? "Online" : "Offline"}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400 flex items-center gap-1">
                <Cloud className="w-3 h-3" />
                Storage
              </span>
              <span className="text-cyan-400 text-right max-w-[140px] truncate">
                {storageMode || "Local"}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-400">Last sync</span>
              <span className="text-gray-300">{formatLastSync(lastSyncTime)}</span>
            </div>
          </div>

          {/* Manual sync button */}
          <button
            onClick={onManualSync}
            disabled={isSyncing || !isOnline}
            className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-xl text-sm font-medium transition-all ${
              isSyncing || !isOnline
                ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                : "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/50"
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? "animate-spin" : ""}`} />
            {isSyncing ? "Syncing..." : "Sync Now"}
          </button>

          {/* Error message */}
          {error && (
            <div className="mt-2 p-2 bg-red-500/20 border border-red-500/50 rounded-lg">
              <p className="text-red-400 text-xs">{error}</p>
            </div>
          )}
        </div>
      )}

      {/* Main floating button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all hover:scale-105 ${
          status.color
        } ${status.animate ? "" : ""}`}
      >
        {/* Pulsing indicator for syncing */}
        {isSyncing && (
          <span className="absolute -inset-1 rounded-full animate-ping opacity-30 bg-yellow-400" />
        )}
        
        <StatusIcon
          className={`w-4 h-4 text-white ${status.animate ? "animate-spin" : ""}`}
        />
        <span className="text-white text-sm font-medium hidden sm:inline">
          {status.text}
        </span>
        <span className="text-white/70 text-xs hidden sm:inline">
          {formatLastSync(lastSyncTime)}
        </span>
        {isExpanded ? (
          <ChevronDown className="w-3 h-3 text-white/70" />
        ) : (
          <ChevronUp className="w-3 h-3 text-white/70" />
        )}
      </button>
    </div>
  );
};

export default SyncStatusIndicator;

